import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import { get } from "lodash-es";
import { FC } from "react";
import { useCallback } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";

import {
  ButtonBase,
  Divider,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  SvgIcon,
  Typography,
} from "@mui/material";

import { usePopover } from "../../hooks/use-popover";
import useResetRedirect from "../../hooks/use-reset-redirect";
import { setClusterAndOrg } from "../../slices/current";
import { useDispatch, useSelector } from "../../store";
import { HostedChip } from "@/components/HostedChip";

export const ClusterDropdown: FC = () => {
  const popover = usePopover<HTMLButtonElement>();
  const dispatch = useDispatch();
  const { byId } = useSelector((state) => state.namespaces);
  const { clustersByOrg } = useSelector((state) => state.organizations);
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const navigate = useNavigate();
  const clusters = get(clustersByOrg, organizationId, []);

  const goto = () => {
    popover.handleClose();
    navigate("/app/clusters");
  };
  const baseRedirect = useResetRedirect();

  const handleClusterChange = useCallback(
    (id: string): void => {
      popover.handleClose();
      const flavor = clusters.find((c) => c.id === id)?.flavor;
      dispatch(setClusterAndOrg({ clusterId: id, organizationId, flavor }));
      navigate(
        {
          pathname: baseRedirect,
          search: createSearchParams({
            oid: organizationId,
            cid: id,
          }).toString(),
        },
        { replace: true },
      );
    },
    [popover, dispatch, clusters, organizationId, baseRedirect, navigate],
  );

  const clusterName =
    (get(byId, clusterId + ".namespace.name", "") as string) || "";

  return (
    <>
      <ButtonBase
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          border: 2,
          borderColor: "divider",
          display: "flex",
          px: "12px",
          py: "6px",
          "&:hover": {
            backgroundColor: "background.default",
          },
          width: "100%",
          justifyContent: "space-between",
          variant: "outlined",
        }}
      >
        <Typography
          component="span"
          color="inherit"
          sx={{
            fontSize: 14,
            lineHeight: "24px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 120,
          }}
          className="flex items-center gap-1"
          title={clusterName}
        >
          <span>{clusterName}</span>
          {clusters.find((c) => c.id === clusterId)?.flavor === "hosted" ? (
            <HostedChip />
          ) : null}
        </Typography>
        <SvgIcon color="action" fontSize="small" sx={{ ml: 3 }}>
          <ChevronDownIcon />
        </SvgIcon>
      </ButtonBase>
      <Popover
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        disableScrollLock
        onClose={popover.handleClose}
        open={popover.open}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              minWidth: 210,
            },
          },
        }}
      >
        <MenuList dense sx={{ p: 0 }}>
          {clusters.map((cluster) => {
            const isSelected = cluster.id === clusterId;

            return (
              <MenuItem
                key={cluster.id}
                onClick={() => handleClusterChange?.(cluster.id)}
                selected={isSelected}
                sx={{ borderRadius: 1 }}
                className={"grid grid-cols-[1fr_auto] gap-1 items-center"}
              >
                <ListItemText>{cluster?.name}</ListItemText>
                {cluster.flavor === "hosted" ? <HostedChip /> : null}
              </MenuItem>
            );
          })}
          <Divider sx={{ my: 1 }} />
          <MenuItem
            key={"manage-clusters"}
            onClick={goto}
            sx={{ borderRadius: 1 }}
          >
            <ListItemText>Manage clusters</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
};
